import { useEffect, useMemo, useState } from 'react';
import { RiPrinterLine, RiCloseLine, RiFingerprintLine, RiAlertLine } from 'react-icons/ri';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { api } from '../state/api';
import { useStore } from '../state/store';
import { useAuthStore } from '../state/authStore';
import {
  buildGraphModel,
  criticalNodes,
  graphSummary,
  communities,
} from '../analysis/graph';
import { analyzeAch, type AchCell } from '../analysis/ach';
import {
  CASE_ROLE_LABEL,
  CLASSIFICATION_LABEL,
  HYPOTHESIS_STATUS_LABEL,
  PRIORITY_LABEL,
  SOURCE_RELIABILITY_SHORT,
  INFO_CREDIBILITY_SHORT,
  TIMELINE_KIND_LABEL,
  type AchMatrix,
} from '../state/domain';
import { formatDate } from '@/lib/utils';

/** 情报简报：可打印 / 另存为 PDF 的一页纸产物。 */
export function BriefView() {
  const open = useStore(state => state.briefOpen);
  const setBriefOpen = useStore(state => state.setBriefOpen);
  const engagement = useStore(state => state.engagements.find(e => e.id === state.activeEngagementId));
  const targets = useStore(state => state.targets);
  const entities = useStore(state => state.entities);
  const relations = useStore(state => state.relations);
  const evidence = useStore(state => state.evidence);
  const hypotheses = useStore(state => state.hypotheses);
  const timeline = useStore(state => state.timeline);
  const requirements = useStore(state => state.requirements);
  const coverage = useStore(state => state.requirementCoverage);
  const user = useAuthStore(state => state.user);

  const [ach, setAch] = useState<AchMatrix | null>(null);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('brief-open');
    return () => document.body.classList.remove('brief-open');
  }, [open]);

  useEffect(() => {
    if (!open || !engagement) return;
    api
      .getAch(engagement.id)
      .then(setAch)
      .catch(() => setAch(null));
  }, [open, engagement]);

  const graph = useMemo(() => buildGraphModel(entities, relations), [entities, relations]);
  const summary = useMemo(() => graphSummary(graph), [graph]);
  const critical = useMemo(() => criticalNodes(graph, 5), [graph]);
  const comm = useMemo(() => communities(graph), [graph]);
  const achResult = useMemo(
    () =>
      ach && ach.hypotheses.length && ach.evidence.length
        ? analyzeAch(ach.cells as AchCell[], ach.hypotheses, ach.evidence)
        : null,
    [ach]
  );

  if (!open || !engagement) return null;

  const topTarget = targets.length ? targets.reduce((a, b) => (b.riskScore > a.riskScore ? b : a)) : null;
  const openRequirements = requirements.filter(r => r.status === 'open' || r.status === 'partial');
  const keyJudgments = achResult
    ? achResult.hypothesisScores.slice(0, 3).map(score => ({
        text: hypotheses.find(h => h.id === score.hypothesisId)?.statement ?? score.statement,
        meta: `否证分 ${score.disconfirmScore} · #${score.rank}`,
        favored: score.rank === 1,
      }))
    : hypotheses
        .slice()
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 3)
        .map(h => ({
          text: h.statement,
          meta: `置信度 ${Math.round(h.confidence * 100)}%`,
          favored: h.status === 'supported',
        }));

  return (
    <div className="brief-overlay fixed inset-0 z-[200] overflow-y-auto bg-[#f5f5f3] p-6 print:static print:overflow-visible print:bg-white print:p-0">
      <div className="mx-auto mb-3 flex max-w-[820px] items-center gap-2 print:hidden">
        <Badge variant="muted">情报简报</Badge>
        <span className="text-[11.5px] text-muted-foreground">
          打印或「另存为 PDF」后即为可归档的成品
        </span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={() => window.print()}>
            <RiPrinterLine className="size-3.5" />
            打印 / 另存为 PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBriefOpen(false)}>
            <RiCloseLine className="size-3.5" />
            关闭
          </Button>
        </div>
      </div>

      <article className="brief-doc mx-auto max-w-[820px] bg-white p-10 shadow-[0_10px_40px_rgba(25,26,28,0.10)] print:max-w-none print:p-0 print:shadow-none">
        {/* 页眉 */}
        <header className="flex items-start gap-3 border-b-2 border-[#1e3a5f] pb-3">
          <div className="grid size-9 shrink-0 place-items-center rounded-[8px] bg-[#1e3a5f] text-white">
            <RiFingerprintLine className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold tracking-[0.22em] text-[#1e3a5f]">EXPOTAR · 情报简报</div>
            <h1 className="mt-0.5 text-[19px] font-semibold leading-snug">{engagement.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10.5px] text-[#6d7176]">
              <span className="font-mono">{engagement.caseNo ?? '—'}</span>
              <span>密级 {CLASSIFICATION_LABEL[engagement.classification]}</span>
              <span>优先级 {PRIORITY_LABEL[engagement.priority]}</span>
              <span>主办 {engagement.lead ?? '—'}</span>
              <span>授权依据 {engagement.legalBasis ?? '—'}</span>
              <span>有效期至 {engagement.expiresAt ? formatDate(engagement.expiresAt).slice(0, 10) : '—'}</span>
            </div>
          </div>
          <div className="shrink-0 text-right text-[9.5px] text-[#6d7176]">
            <div>生成 {formatDate(new Date().toISOString())}</div>
            <div>制作 {user?.name ?? '—'}</div>
            <div>{user ? CASE_ROLE_LABEL.owner : ''}</div>
          </div>
        </header>

        {/* 数据概要 */}
        <div className="mt-3 grid grid-cols-6 gap-1.5 text-center">
          {[
            ['对象', targets.length],
            ['线索', entities.length],
            ['关系', relations.length],
            ['证据', evidence.length],
            ['研判', hypotheses.length],
            ['事件', timeline.length],
          ].map(([label, value]) => (
            <div key={label as string} className="rounded border border-[#e4e2dc] py-1.5">
              <div className="font-mono text-[14px] font-semibold">{value as number}</div>
              <div className="text-[9px] text-[#6d7176]">{label as string}</div>
            </div>
          ))}
        </div>

        {/* 关键判断 */}
        <Section title="一、关键判断" hint={achResult ? '按 ACH 否证分排序（越靠前越可信）' : '按研判置信度排序'}>
          {keyJudgments.length === 0 && <Empty />}
          <ol className="flex flex-col gap-1.5">
            {keyJudgments.map((judgment, index) => (
              <li key={index} className="flex gap-2">
                <span
                  className="mt-1 grid size-4 shrink-0 place-items-center rounded-full text-[9px] font-semibold text-white"
                  style={{ background: judgment.favored ? '#0f7a5a' : '#6d7176' }}
                >
                  {index + 1}
                </span>
                <span>
                  <span className="text-[12px] leading-relaxed">{judgment.text}</span>
                  <span className="ml-2 text-[10px] text-[#6d7176]">{judgment.meta}</span>
                </span>
              </li>
            ))}
          </ol>
        </Section>

        {/* 对象与风险 */}
        <Section title="二、对象与风险">
          {targets.length === 0 && <Empty />}
          <table className="w-full border-collapse text-[10.5px]">
            <thead>
              <tr className="bg-[#f2f1ee] text-left">
                <th className="border border-[#e4e2dc] px-2 py-1">对象</th>
                <th className="border border-[#e4e2dc] px-2 py-1">职务 / 组织</th>
                <th className="border border-[#e4e2dc] px-2 py-1">风险</th>
                <th className="border border-[#e4e2dc] px-2 py-1">状态</th>
              </tr>
            </thead>
            <tbody>
              {targets.slice(0, 8).map(target => (
                <tr key={target.id}>
                  <td className="border border-[#e4e2dc] px-2 py-1 font-medium">
                    {target.name}
                    {target.codename ? `（${target.codename}）` : ''}
                  </td>
                  <td className="border border-[#e4e2dc] px-2 py-1">
                    {[target.role, target.org].filter(Boolean).join(' / ') || '—'}
                  </td>
                  <td className="border border-[#e4e2dc] px-2 py-1 font-mono">
                    {target.riskScore} · {target.riskLevel}
                  </td>
                  <td className="border border-[#e4e2dc] px-2 py-1">{target.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {topTarget && (
            <p className="mt-1.5 text-[10.5px] leading-relaxed text-[#3f4247]">
              最高风险对象为 <b>{topTarget.name}</b>（{topTarget.riskScore} / {topTarget.riskLevel}）
              {topTarget.summary ? `；${topTarget.summary}` : ''}
            </p>
          )}
        </Section>

        {/* 关联网络 */}
        <Section title="三、关联网络要点">
          <p className="text-[10.5px] text-[#3f4247]">
            节点 {summary.nodeCount} · 边 {summary.edgeCount} · 密度 {summary.density.toFixed(3)} · 平均度{' '}
            {summary.averageDegree.toFixed(2)} · 社群 {summary.communityCount} · 割点 {summary.articulationCount} ·
            桥接关系 {summary.bridgeCount} · 孤立 {summary.isolateCount}
          </p>
          <ol className="mt-1.5 flex flex-col gap-1">
            {critical.map((node, index) => (
              <li key={node.id} className="flex items-center gap-2 text-[10.5px]">
                <span className="font-mono text-[9.5px] text-[#6d7176]">#{index + 1}</span>
                <span className="font-medium">{node.label}</span>
                <span className="text-[#6d7176]">
                  度 {node.degree} · 介数 {node.betweenness.toFixed(1)} · 社群 {node.community + 1}
                </span>
                {node.isArticulation && (
                  <span className="rounded bg-[#b3261e]/10 px-1 text-[9px] text-[#b3261e]">割点</span>
                )}
              </li>
            ))}
          </ol>
          {critical.length === 0 && <Empty />}
          <p className="mt-1 text-[9.5px] text-[#6d7176]">
            社群划分：{[...new Set([...comm.values()])].length} 组；割点与桥接关系为「打掉即断裂」的关键位置。
          </p>
        </Section>

        {/* 时间线 */}
        <Section title="四、事件时间线">
          {timeline.length === 0 && <Empty />}
          <ul className="flex flex-col gap-1">
            {timeline.slice(0, 10).map(event => (
              <li key={event.id} className="flex gap-2 text-[10.5px]">
                <span className="w-[86px] shrink-0 font-mono text-[9.5px] text-[#6d7176]">
                  {formatDate(event.occurredAt).slice(0, 10)}
                </span>
                <span className="shrink-0 rounded bg-[#f2f1ee] px-1 text-[9px]">
                  {TIMELINE_KIND_LABEL[event.kind]}
                </span>
                <span>
                  {event.title}
                  {event.location ? <span className="text-[#6d7176]"> @{event.location}</span> : ''}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        {/* 证据 */}
        <Section title="五、证据清单（Admiralty 双轴）">
          {evidence.length === 0 && <Empty />}
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-[#f2f1ee] text-left">
                <th className="border border-[#e4e2dc] px-2 py-1 w-[62px]">评级</th>
                <th className="border border-[#e4e2dc] px-2 py-1">来源 / 内容</th>
                <th className="border border-[#e4e2dc] px-2 py-1 w-[80px]">类型</th>
                <th className="border border-[#e4e2dc] px-2 py-1 w-[92px]">采集时间</th>
              </tr>
            </thead>
            <tbody>
              {evidence.slice(0, 14).map(item => (
                <tr key={item.id}>
                  <td className="border border-[#e4e2dc] px-2 py-1 font-mono text-center">
                    {item.sourceReliability}
                    {item.infoCredibility}
                  </td>
                  <td className="border border-[#e4e2dc] px-2 py-1">
                    <div className="truncate">{item.title ?? item.url ?? '(无标题)'}</div>
                    <div className="text-[9px] text-[#6d7176]">
                      {SOURCE_RELIABILITY_SHORT[item.sourceReliability]} /{' '}
                      {INFO_CREDIBILITY_SHORT[item.infoCredibility]}
                    </div>
                  </td>
                  <td className="border border-[#e4e2dc] px-2 py-1">{item.kind}</td>
                  <td className="border border-[#e4e2dc] px-2 py-1 font-mono text-[9px]">
                    {formatDate(item.collectedAt).slice(0, 16)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* 情报缺口 */}
        <Section title="六、情报缺口与下一步">
          <p className="text-[10.5px] text-[#3f4247]">
            需求覆盖度 {coverage?.coverage ?? 0}% · 待答复 {coverage?.open ?? 0} · 高优先未答{' '}
            {coverage?.highOpen ?? 0} · 逾期 {coverage?.overdue ?? 0}
          </p>
          {openRequirements.length > 0 ? (
            <ul className="mt-1.5 flex flex-col gap-1">
              {openRequirements.slice(0, 6).map(item => (
                <li key={item.id} className="flex items-start gap-2 text-[10.5px]">
                  <RiAlertLine className="mt-0.5 size-3 shrink-0 text-[#b3261e]" />
                  <span>
                    <span className="font-mono text-[9.5px] text-[#6d7176]">{item.code}</span> {item.question}
                    <span className="ml-1 text-[9px] text-[#6d7176]">（{item.statusLabel}）</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-[10.5px] text-[#0f7a5a]">当前没有未答复的高优先需求。</p>
          )}
        </Section>

        <footer className="mt-5 border-t border-[#e4e2dc] pt-2.5 text-[9px] leading-relaxed text-[#6d7176]">
          <p>
            本简报基于授权范围内的公开信息与内部登记数据编制，所有结论均标注依据与置信度；
            标注为「推断」的内容不构成事实认定。分发与保存请遵守所在组织的密级管理规定。
          </p>
          <p className="mt-0.5">
            授权依据：{engagement.legalBasis ?? '—'} · 检索范围：
            {[
              ...(engagement.scope.domains ?? []),
              ...(engagement.scope.ips ?? []),
              ...(engagement.scope.emails ?? []),
            ]
              .slice(0, 6)
              .join('、') || '—'}
          </p>
        </footer>
      </article>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 break-inside-avoid">
      <h2 className="flex items-center gap-2 border-b border-[#e4e2dc] pb-1 text-[12px] font-semibold text-[#1e3a5f]">
        {title}
        {hint && <span className="text-[9px] font-normal text-[#6d7176]">{hint}</span>}
      </h2>
      <div className="mt-1.5">{children}</div>
    </section>
  );
}

function Empty() {
  return <p className="text-[10.5px] text-[#6d7176]">无</p>;
}

export { HYPOTHESIS_STATUS_LABEL };